import React, { useState, useEffect } from 'react';
import { Package, Edit, Trash2, ArrowUp, ArrowDown, Info, Store, Lock, Sparkles, Tag } from 'lucide-react';
import { useLang } from '../../core/lang.jsx';
import LangKeys from '../../core/locales/keys';
import StockUpdateModal from './StockUpdateModal';
import itemService from '../../services/itemService';
import billService from '../../services/billService';
import { getCategoryDetails } from './CategoryPickerModal';

const ProductDetailsView = ({ item, onEdit, onDelete, onClose, onStockUpdated }) => {
  const { t } = useLang();
  const [showStockModal, setShowStockModal] = useState(false);
  const [isStockIn, setIsStockIn] = useState(true);
  const [bills, setBills] = useState([]);
  const [loadingBills, setLoadingBills] = useState(false);

  useEffect(() => {
    const fetchBills = async () => {
      setLoadingBills(true);
      try {
        const data = await billService.fetchBills();
        if (data) setBills(data);
      } catch (err) {
        console.error('Failed to load bills for item history:', err);
      } finally {
        setLoadingBills(false);
      }
    };
    fetchBills();
  }, [item.id]);

  if (!item) return null;

  const handleStockClick = (stockIn) => {
    setIsStockIn(stockIn);
    setShowStockModal(true);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return parseFloat(num).toFixed(2);
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return new Date();
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return new Date(dateStr + 'T00:00:00');
    }
    return new Date(dateStr);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTransactions = () => {
    const txs = [];

    // 1. Add initial opening stock if it exists
    const openingStock = parseFloat(item.opening_stock || 0);
    txs.push({
      date: item.as_of_date ? parseDate(item.as_of_date) : new Date(),
      type: 'Opening Stock',
      quantity: openingStock,
      rate: parseFloat(item.purchase_price) || 0.0,
      partyName: t(LangKeys.opening_stock) || 'Opening Stock',
      billNumber: '',
      isOpening: true,
    });

    // 2. Add transactions from bills
    bills.forEach(bill => {
      const billItems = bill.items || [];
      billItems.forEach(billItem => {
        const isMatch = (billItem.item?.id?.toString() === item.id?.toString()) || 
                        (billItem.item_id?.toString() === item.id?.toString()) ||
                        (billItem.id?.toString() === item.id?.toString());
        if (isMatch) {
          txs.push({
            date: new Date(bill.date || bill.created_at),
            type: bill.type === 'sale' ? 'Out' : 'In',
            quantity: parseFloat(billItem.quantity || 0),
            rate: parseFloat(billItem.price || 0),
            partyName: bill.customer?.customerName || bill.party_name || 'Party',
            billNumber: bill.billNumber || bill.bill_no || '',
            isOpening: false,
          });
        }
      });
    });

    // 3. Sort by date ascending to calculate running balance
    txs.sort((a, b) => a.date - b.date);

    let currentBalance = 0.0;
    txs.forEach(tx => {
      if (tx.isOpening) {
        currentBalance = tx.quantity;
      } else {
        if (tx.type === 'In') {
          currentBalance += tx.quantity;
        } else {
          currentBalance -= tx.quantity;
        }
      }
      tx.runningBalance = currentBalance;
    });

    // 4. Sort by date descending (latest first) for UI
    txs.sort((a, b) => b.date - a.date);

    return txs;
  };

  const txs = getTransactions();
  const currentStock = txs.length > 0 ? txs[0].runningBalance : parseFloat(item.opening_stock || 0);
  const stockValue = currentStock * parseFloat(item.sale_price || 0);
  const isOnline = item.is_public !== 0 && item.is_public !== false && item.is_public !== '0';
  const catInfo = getCategoryDetails(item.category, false, t);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Top Header */}
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <p style={styles.label}>{t(LangKeys.item_name)}</p>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '700',
                  backgroundColor: isOnline ? '#DCFCE7' : '#F1F5F9',
                  color: isOnline ? '#16A34A' : '#64748B',
                  border: `1px solid ${isOnline ? '#BBF7D0' : '#E2E8F0'}`
                }}
              >
                {isOnline ? <Store size={12} /> : <Lock size={12} />}
                {isOnline ? (t(LangKeys.online_active) || 'Online in Market') : (t(LangKeys.online_private) || 'Private')}
              </span>
            </div>
            <h2 style={styles.itemName}>{item.name}</h2>
          </div>
          <div style={styles.photoBox}>
            {item.photo_url ? (
              <img src={item.photo_url} alt="" style={styles.img} />
            ) : (
              <Package size={32} color="#94A3B8" />
            )}
          </div>
        </div>

        {/* Marketplace & Category Banner */}
        <div style={{
          backgroundColor: '#FFF',
          padding: '12px 16px',
          borderRadius: '14px',
          border: '1px solid #E2E8F0',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>{catInfo.icon}</span>
            <div>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase' }}>
                {t(LangKeys.item_category_lbl) || 'Category'}
              </span>
              <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>
                {catInfo.title} <span style={{ fontSize: '12px', fontWeight: '400', color: '#64748B' }}>({catInfo.subtitle})</span>
              </div>
            </div>
          </div>
          {parseFloat(item.discount_price || 0) > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#EFF6FF',
              padding: '6px 12px',
              borderRadius: '8px',
              border: '1px solid #BFDBFE'
            }}>
              <Tag size={14} color="#2563EB" />
              <span style={{ fontSize: '12px', fontWeight: '700', color: '#2563EB' }}>
                Offer Price: ₹{formatNumber(item.discount_price)}
              </span>
            </div>
          )}
        </div>

        {/* Optional Description / SEO */}
        {item.description && (
          <div style={{
            backgroundColor: '#F8FAFC',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <Sparkles size={12} color="#2563EB" />
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748B' }}>
                {t(LangKeys.item_desc_lbl) || 'Product Description / SEO'}
              </span>
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: '#334155', lineHeight: '1.4' }}>
              {item.description}
            </p>
          </div>
        )}

        <div style={styles.grid}>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.sale_price)}</p>
            <p style={styles.value}>₹{formatNumber(item.sale_price)}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.purchase_price)}</p>
            <p style={styles.value}>₹{formatNumber(item.purchase_price)}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.stock_quantity)}</p>
            <p style={styles.value}>{formatNumber(currentStock)} {item.unit || ''}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.stock_value)}</p>
            <p style={styles.value}>₹{formatNumber(stockValue)}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.primary_unit)}</p>
            <p style={styles.value}>{item.unit || 'N/A'}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.low_stock)}</p>
            <p style={styles.value}>{formatNumber(item.low_stock_alert)}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.hsn_code)}</p>
            <p style={styles.value}>{item.hsn_sac_code || '-'}</p>
          </div>
          <div style={styles.cell}>
            <p style={styles.label}>{t(LangKeys.gst_rate_percent)}</p>
            <p style={styles.value}>{item.gst_rate ? `${item.gst_rate}%` : '-'}</p>
          </div>
        </div>

        <div style={styles.actions}>
           <button style={styles.editBtn} onClick={() => onEdit(item)}>
             <Edit size={18} /> {t(LangKeys.edit_item)}
           </button>
           <button 
             style={styles.deleteBtn} 
             onClick={async () => {
               if (window.confirm(t(LangKeys.delete_confirmation) || "Are you sure you want to delete this product?")) {
                 const success = await itemService.deleteItem(item.id);
                 if (success) {
                   onClose();
                   if (onStockUpdated) onStockUpdated();
                 }
               }
             }}
           >
             <Trash2 size={18} />
           </button>
        </div>

        {/* STOCK HISTORY SECTION */}
        <div style={styles.historySection}>
          <div style={styles.historyHeader}>
            <h3 style={styles.historyTitle}>{t(LangKeys.stock_history) || 'Stock History'}</h3>
            <span style={styles.historyCount}>
              {txs.length} {txs.length === 1 ? t(LangKeys.entries) || 'Entry' : t(LangKeys.entries) || 'Entries'}
            </span>
          </div>

          {loadingBills ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ margin: '0 auto', width: '30px', height: '30px', border: '3px solid #E2E8F0', borderTop: '3px solid #2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : txs.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>{t(LangKeys.bills_no_matching_found) || 'No history found'}</p>
            </div>
          ) : (
            <div style={styles.txList}>
              {txs.map((tx, idx) => {
                const isOpening = tx.isOpening;
                const isOut = tx.type === 'Out';
                
                let iconColor = '#3B82F6';
                let iconBg = '#EFF6FF';
                let IconComponent = Info;
                
                if (!isOpening) {
                  if (isOut) {
                    iconColor = '#EF4444';
                    iconBg = '#FEF2F2';
                    IconComponent = ArrowUp;
                  } else {
                    iconColor = '#10B981';
                    iconBg = '#ECFDF5';
                    IconComponent = ArrowDown;
                  }
                }

                const displayTitle = isOpening
                  ? tx.partyName
                  : tx.billNumber
                  ? `${tx.partyName} (Bill #${tx.billNumber})`
                  : tx.partyName;

                return (
                  <div key={idx} style={styles.txItem}>
                    <div style={{ ...styles.iconWrapper, backgroundColor: iconBg, color: iconColor }}>
                      <IconComponent size={18} />
                    </div>
                    <div style={styles.txInfo}>
                      <span style={styles.txTitle}>{displayTitle}</span>
                      <span style={styles.txMeta}>{formatDate(tx.date)}</span>
                      <span style={styles.txSub}>
                        {t(LangKeys.balance) || 'Balance'}: {formatNumber(tx.runningBalance)} {item.unit || ''}
                      </span>
                    </div>
                    <div style={styles.txRight}>
                      <span style={{ ...styles.txQty, color: iconColor }}>
                        {!isOpening && (isOut ? '-' : '+')}
                        {formatNumber(tx.quantity)} {item.unit || ''}
                      </span>
                      <span style={styles.txRate}>₹{formatNumber(tx.rate)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={styles.bottomBar}>
        <button style={styles.stockOutBtn} onClick={() => handleStockClick(false)}>
          - {t(LangKeys.stock_out_caps)}
        </button>
        <button style={styles.stockInBtn} onClick={() => handleStockClick(true)}>
          + {t(LangKeys.stock_in_caps)}
        </button>
      </div>

      <StockUpdateModal 
        isOpen={showStockModal} 
        onClose={() => setShowStockModal(false)}
        item={item}
        isStockIn={isStockIn}
        onStockUpdated={onStockUpdated}
      />
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  container: { height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#F8FAFC' },
  content: { flex: 1, padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' },
  titleSection: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '12px', fontWeight: '700', color: '#94A3B8', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' },
  itemName: { fontSize: '32px', fontWeight: '900', color: '#0F172A', margin: 0 },
  photoBox: { width: '80px', height: '80px', backgroundColor: '#F1F5F9', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E2E8F0', overflow: 'hidden' },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#FFF' },
  cell: { padding: '20px', borderRight: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', gap: '8px' },
  value: { fontSize: '18px', fontWeight: '800', color: '#1E293B', margin: 0 },
  actions: { display: 'flex', gap: '12px', marginTop: '32px', justifyContent: 'flex-end' },
  editBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#FFF', border: '1px solid #E2E8F0', borderRadius: '12px', color: '#2563EB', fontWeight: '700', cursor: 'pointer', transition: '0.2s' },
  deleteBtn: { padding: '12px', backgroundColor: '#FEF2F2', border: '1px solid #FEE2E2', borderRadius: '12px', color: '#DC2626', cursor: 'pointer' },
  bottomBar: { padding: '20px 32px', backgroundColor: '#FFF', borderTop: '1px solid #E2E8F0', display: 'flex', gap: '16px' },
  stockInBtn: { flex: 1, height: '54px', backgroundColor: '#16A34A', color: '#FFF', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)' },
  stockOutBtn: { flex: 1, height: '54px', backgroundColor: '#DC2626', color: '#FFF', border: 'none', borderRadius: '16px', fontWeight: '800', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)' },
  historySection: { marginTop: '32px', backgroundColor: '#FFF', borderRadius: '16px', border: '1px solid #E2E8F0', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' },
  historyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  historyTitle: { fontSize: '18px', fontWeight: '800', color: '#0F172A', margin: 0 },
  historyCount: { fontSize: '12px', fontWeight: '600', color: '#64748B', backgroundColor: '#F1F5F9', padding: '4px 12px', borderRadius: '20px' },
  emptyState: { padding: '40px', textAlign: 'center', backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px dashed #E2E8F0', color: '#64748B' },
  txList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  txItem: { display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0', transition: 'all 0.2s ease' },
  iconWrapper: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  txInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
  txTitle: { fontSize: '14px', fontWeight: '700', color: '#1E293B' },
  txMeta: { fontSize: '11px', fontWeight: '500', color: '#94A3B8' },
  txSub: { fontSize: '12px', fontWeight: '600', color: '#64748B' },
  txRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' },
  txQty: { fontSize: '14px', fontWeight: '800' },
  txRate: { fontSize: '12px', fontWeight: '600', color: '#64748B' },
};

export default ProductDetailsView;
